import React, { type ReactNode, useEffect, useMemo, useRef, useState } from "react"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import Card from "../Card/Card"
import style from "./List.module.css"
import InputField from "../InputField/InputField"
import {
  faChevronLeft,
  faChevronRight,
  faFileImport,
  faMagnifyingGlass,
  faMessage,
  faPlus,
  faRetweet,
} from "@fortawesome/free-solid-svg-icons"
import { GenericDropDown } from "../Dropdown/Dropdown"
import GetSpace from "../../utils/space"
import { CountElements } from "../CountElements/CountElements"
import { MessageList } from "../MessageList/MessageList"
import { FloatButton } from "../FloatButton/FloatButton"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { useMenuMobileContext } from "../../context/useMenuMobileContext"
import { EOrdenationType } from "../../Enum/EOrdenationType"
import { EEnableStatusType } from "../../Enum/EEnableStatusType"
import { useOnboardingCreatePopup } from "../../hooks/useOnboardingCreatePopup"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTranslation } from "react-i18next"

const LOCAL_PAGE_SIZE = 36

export interface ListItem {
  id: number
  title: string
  linkImage: string
  index: number
  isDisable?: boolean
  dataFilter?: string[]
  children?: ReactNode
  body?: ReactNode
  canUse?: boolean
}

interface ListProps {
  messageCount: string
  messageEmpty: string
  nameFindSearch: string
  showFilterStatus?: boolean
  handleAddButton?: () => void
  handleRefreshButton?: () => void
  handleOnClick: (id: number) => void
  disableFilters?: true
  nameDropdown?: string[]
  defaultStatusFilter?: string
  data: ListItem[]
  dataChildren?: ReactNode
  enablePullToRefresh?: boolean | false
  handleExtraFloatButton?: () => void
  extraFloatButtonDescriptionKey?: string
  extraFloatButtonColor?: string
  extraFloatButtonIcon?: IconProp
  handleSecondaryExtraFloatButton?: () => void
  secondaryExtraFloatButtonDescriptionKey?: string
  secondaryExtraFloatButtonColor?: string
  secondaryExtraFloatButtonIcon?: IconProp
  handleImportFloatButton?: () => void
  importFloatButtonDescriptionKey?: string
  importFloatButtonColor?: string
  importFloatButtonIcon?: IconProp
  hasMore?: boolean
  isLoadingMore?: boolean
  onLoadMore?: () => void
  searchValue?: string
  onSearchChange?: (value: string) => void
  disableLocalSearchFilter?: boolean
  disableLocalOrdenation?: boolean
  enableLocalPagination?: boolean
  filterActions?: ReactNode
  countFooter?: ReactNode
}

const List: React.FC<ListProps> = ({
  enablePullToRefresh,
  dataChildren,
  nameDropdown,
  defaultStatusFilter,
  disableFilters,
  data,
  showFilterStatus = true,
  handleOnClick,
  messageEmpty,
  messageCount,
  handleAddButton,
  handleRefreshButton,
  handleExtraFloatButton,
  extraFloatButtonDescriptionKey,
  extraFloatButtonColor = "#FFB45C",
  extraFloatButtonIcon,
  handleSecondaryExtraFloatButton,
  secondaryExtraFloatButtonDescriptionKey,
  secondaryExtraFloatButtonColor = "#3498db",
  secondaryExtraFloatButtonIcon,
  handleImportFloatButton,
  importFloatButtonDescriptionKey = "interactionDescriptions:components.list.list.button.handleImportFloatButton",
  importFloatButtonColor = "#2563eb",
  importFloatButtonIcon = faFileImport,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  searchValue,
  onSearchChange,
  disableLocalSearchFilter = false,
  disableLocalOrdenation = false,
  enableLocalPagination = false,
  nameFindSearch,
  filterActions,
  countFooter,
}) => {
  const { isNotMobile } = useMenuMobileContext()
  const { t } = useTranslation("list")

  useOnboardingCreatePopup(handleAddButton)

  const [nameFind, setNameFind] = useState("")

  const effectiveNameFind = searchValue ?? nameFind

  const [statusCard, setStatusCard] = useState(
    defaultStatusFilter ??
      (nameDropdown !== undefined ? nameDropdown[0] : EEnableStatusType.Active.toEnableStatusTypeString()),
  )

  const [ordenation] = useState(Object.mapEnumToStrings(EOrdenationType))

  const [ordenationType, setOrdenationType] = useState(EOrdenationType.Crescent.toOrderType())

  const [currentPage, setCurrentPage] = useState(1)

  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  const hasRefreshFloatButton = !enablePullToRefresh && !!handleRefreshButton

  const hasExtraFloatButton =
    !enablePullToRefresh && !!handleExtraFloatButton && !!extraFloatButtonIcon && !!extraFloatButtonDescriptionKey

  const hasSecondaryExtraFloatButton =
    !enablePullToRefresh &&
    !!handleSecondaryExtraFloatButton &&
    !!secondaryExtraFloatButtonIcon &&
    !!secondaryExtraFloatButtonDescriptionKey

  const hasImportFloatButton = !enablePullToRefresh && !!handleImportFloatButton && !!importFloatButtonIcon

  const getFloatButtonBottom = (visibleButtonsBelow: number) => {
    const baseBottom = isNotMobile ? 5 : 70
    const spacing = isNotMobile ? 90 : 55

    return `${baseBottom + visibleButtonsBelow * spacing}px`
  }

  const visibleFloatButtonsCount =
    (handleAddButton ? 1 : 0) +
    (hasRefreshFloatButton ? 1 : 0) +
    (hasExtraFloatButton ? 1 : 0) +
    (hasSecondaryExtraFloatButton ? 1 : 0) +
    (hasImportFloatButton ? 1 : 0)

  useEffect(() => {
    if (visibleFloatButtonsCount <= 0) {
      return
    }

    document.documentElement.style.setProperty(
      "--operational-help-restaurant-bottom",
      getFloatButtonBottom(visibleFloatButtonsCount),
    )
    document.documentElement.style.setProperty("--operational-help-restaurant-right", "5px")

    return () => {
      document.documentElement.style.removeProperty("--operational-help-restaurant-bottom")
      document.documentElement.style.removeProperty("--operational-help-restaurant-right")
    }
  }, [visibleFloatButtonsCount, isNotMobile])

  const dataList = useMemo(() => {
    const allStatus = nameDropdown !== undefined ? nameDropdown[2] : EEnableStatusType.All.toEnableStatusTypeString()

    const activeStatus =
      nameDropdown !== undefined ? nameDropdown[0] : EEnableStatusType.Active.toEnableStatusTypeString()

    const inactiveStatus =
      nameDropdown !== undefined ? nameDropdown[1] : EEnableStatusType.Inactive.toEnableStatusTypeString()

    const normalizedSearch = effectiveNameFind.trim().toUpperCase()

    return data
      .filter((item) => {
        if (!showFilterStatus || statusCard === allStatus) return true
        if (statusCard === activeStatus) return item.isDisable === false
        if (statusCard === inactiveStatus) return item.isDisable === true

        return true
      })
      .filter(
        (item) =>
          disableLocalSearchFilter ||
          normalizedSearch.length === 0 ||
          item.title.toUpperCase().includes(normalizedSearch) ||
          item.dataFilter?.some((filter) => filter.toUpperCase().includes(normalizedSearch)),
      )
      .sort((a, b) => {
        if (disableLocalOrdenation) {
          return a.index - b.index
        }

        return ordenationType === EOrdenationType.Crescent.toOrderType()
          ? a.title.localeCompare(b.title)
          : b.title.localeCompare(a.title)
      })
  }, [
    data,
    disableLocalOrdenation,
    disableLocalSearchFilter,
    effectiveNameFind,
    nameDropdown,
    ordenationType,
    showFilterStatus,
    statusCard,
  ])

  const visibleDataList = useMemo(() => {
    if (!enableLocalPagination) {
      return dataList
    }

    const startIndex = (currentPage - 1) * LOCAL_PAGE_SIZE

    return dataList.slice(startIndex, startIndex + LOCAL_PAGE_SIZE)
  }, [currentPage, dataList, enableLocalPagination])

  const totalPages = enableLocalPagination ? Math.ceil(dataList.length / LOCAL_PAGE_SIZE) : 1

  useEffect(() => {
    setCurrentPage(1)
  }, [effectiveNameFind, ordenationType, statusCard])

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(Math.max(totalPages, 1))
    }
  }, [currentPage, totalPages])

  useEffect(() => {
    const loadMoreElement = loadMoreRef.current

    if (!loadMoreElement || !onLoadMore || !hasMore || isLoadingMore) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadMore()
        }
      },
      { rootMargin: "600px 0px" },
    )

    observer.observe(loadMoreElement)

    return () => observer.disconnect()
  }, [hasMore, isLoadingMore, onLoadMore])

  return (
    <>
      {!disableFilters && (
        <div className={style.Geral}>
          <Animate>
            <div className={style.inputSearchFilter}>
              <div className={style.containerSearchButton}>
                <div className={style.inputSearch}>
                  <InputField
                    icon={faMagnifyingGlass}
                    value={effectiveNameFind}
                    type={"text"}
                    placeholder={nameFindSearch}
                    animationTypes={AnimationTypes.slideUpIn}
                    onChange={(event) => {
                      onSearchChange?.(event.target.value)
                      setNameFind(event.target.value)
                    }}
                    descriptionKey="interactionDescriptions:components.list.list.input.nameFindSearch"
                  />
                </div>

                {dataChildren && <div className={style.data}>{dataChildren}</div>}

                <div className={style.dropdownContainer}>
                  {showFilterStatus && (
                    <>
                      <GenericDropDown
                        data={nameDropdown ?? Object.mapEnumToStrings(EEnableStatusType)}
                        animationTypes={AnimationTypes.slideUpIn}
                        animationDuration="1.3s"
                        initialState={statusCard}
                        handlerFuncion={(event) => setStatusCard(event.target.value)}
                        descriptionKey="interactionDescriptions:components.list.list.select.select"
                      />

                      <GetSpace right={5} />
                    </>
                  )}

                  <GenericDropDown
                    data={ordenation}
                    animationDuration="1.3s"
                    animationTypes={AnimationTypes.slideUpIn}
                    initialState={ordenationType}
                    handlerFuncion={(event) => setOrdenationType(event.target.value)}
                    descriptionKey="interactionDescriptions:components.list.list.select.select2"
                  />

                  {filterActions && <div className={style.filterActions}>{filterActions}</div>}
                </div>
              </div>
            </div>
          </Animate>

          {handleAddButton && (
            <FloatButton
              icon={faPlus}
              color="#FFB45C"
              onClick={handleAddButton}
              bottom={getFloatButtonBottom(0)}
              right={"5px"}
              iconSize={isNotMobile ? "3x" : "2x"}
              size={isNotMobile ? 85 : 50}
              descriptionKey="interactionDescriptions:components.list.list.button.handleAddButton"
            />
          )}

          {hasRefreshFloatButton && (
            <>
              <GetSpace left={5} />

              <FloatButton
                color="red"
                icon={faRetweet}
                onClick={handleRefreshButton}
                size={isNotMobile ? 85 : 50}
                bottom={getFloatButtonBottom(handleAddButton ? 1 : 0)}
                right={"5px"}
                iconSize={isNotMobile ? "3x" : "2x"}
                descriptionKey="interactionDescriptions:components.list.list.button.handleRefreshButton"
              />
            </>
          )}

          {hasExtraFloatButton && extraFloatButtonDescriptionKey && (
            <>
              <GetSpace left={5} />

              <FloatButton
                color={extraFloatButtonColor}
                icon={extraFloatButtonIcon}
                onClick={handleExtraFloatButton}
                size={isNotMobile ? 85 : 50}
                bottom={getFloatButtonBottom((handleAddButton ? 1 : 0) + (hasRefreshFloatButton ? 1 : 0))}
                right={"5px"}
                iconSize={isNotMobile ? "3x" : "2x"}
                descriptionKey={extraFloatButtonDescriptionKey}
              />
            </>
          )}

          {hasSecondaryExtraFloatButton && secondaryExtraFloatButtonDescriptionKey && (
            <>
              <GetSpace left={5} />

              <FloatButton
                color={secondaryExtraFloatButtonColor}
                icon={secondaryExtraFloatButtonIcon}
                onClick={handleSecondaryExtraFloatButton}
                size={isNotMobile ? 85 : 50}
                bottom={getFloatButtonBottom(
                  (handleAddButton ? 1 : 0) + (hasRefreshFloatButton ? 1 : 0) + (hasExtraFloatButton ? 1 : 0),
                )}
                right={"5px"}
                iconSize={isNotMobile ? "3x" : "2x"}
                descriptionKey={secondaryExtraFloatButtonDescriptionKey}
              />
            </>
          )}

          {hasImportFloatButton && (
            <>
              <GetSpace left={5} />

              <FloatButton
                color={importFloatButtonColor}
                icon={importFloatButtonIcon}
                onClick={handleImportFloatButton}
                size={isNotMobile ? 85 : 50}
                bottom={getFloatButtonBottom(
                  (handleAddButton ? 1 : 0) +
                    (hasRefreshFloatButton ? 1 : 0) +
                    (hasExtraFloatButton ? 1 : 0) +
                    (hasSecondaryExtraFloatButton ? 1 : 0),
                )}
                right={"5px"}
                iconSize={isNotMobile ? "3x" : "2x"}
                descriptionKey={importFloatButtonDescriptionKey}
              />
            </>
          )}

          <GetSpace top={5} />
        </div>
      )}

      <div className={style.list}>
        <CountElements
          count={dataList.length}
          title={messageCount}
          animationTypes={AnimationTypes.slideUpIn}
          animationDuration="2s"
        />

        {countFooter}

        <GetSpace top={5} />

        {dataList.length === 0 && (
          <>
            <MessageList
              color="#ffffff"
              message={messageEmpty}
              icon={faMessage}
              animationTypes={AnimationTypes.slideUpIn}
              animationDuration="3s"
            />

            <GetSpace top={5} />
          </>
        )}

        <div className={style.listContainer}>
          {visibleDataList.map((item) => (
            <div key={item.id} className={style.listItem}>
              <Card
                isDisable={item.isDisable}
                keyData={item.id}
                children={item.children}
                body={item.body}
                linkImage={item.linkImage}
                onClick={(e) => {
                  e.stopPropagation()
                  handleOnClick(item.id)
                }}
                descriptionKey="interactionDescriptions:components.list.list.card.openItem"
              />
            </div>
          ))}
        </div>

        {enableLocalPagination && totalPages > 1 && (
          <nav className={style.pagination} aria-label={t("pagination_aria_label")}>
            <button
              type="button"
              className={style.paginationButton}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
              data-interaction-description-key="interactionDescriptions:components.list.list.button.setCurrentPagePageMathMaxPage11"
            >
              <FontAwesomeIcon icon={faChevronLeft} />

              <span>{t("pagination_previous")}</span>
            </button>

            <div className={style.paginationStatus} aria-live="polite">
              <span>{t("pagination_page")}</span>

              <strong>{currentPage}</strong>

              <span>{t("pagination_of", { totalPages })}</span>
            </div>

            <button
              type="button"
              className={style.paginationButton}
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
              data-interaction-description-key="interactionDescriptions:components.list.list.button.setCurrentPagePageMathMinPage1TotalPages"
            >
              <span>{t("pagination_next")}</span>

              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </nav>
        )}

        <div ref={loadMoreRef} className={style.loadMoreSentinel}>
          {isLoadingMore && <span className={style.loadMoreSpinner} />}
        </div>
      </div>
    </>
  )
}

export default List
